/**
 * ONE LOAN: the schedule, the repayments, the collateral, and how to pay (FR-U-CRD-005, 006, 007).
 *
 * ## FOUR THINGS THIS SCREEN REFUSES TO BLUR
 *
 *   1. APPROVED IS NOT DISBURSED. A loan can exist with no money moved. The screen says which.
 *   2. THE COLLATERAL LOCK IS LIVE, NOT DECORATIVE. FR-U-CRD-006 requires the borrower to see the
 *      lock status of their pledged units, and it changes the moment the platform changes it.
 *   3. ARREARS IS NOT DEFAULT. A missed payment shows as overdue with the grace period and its end
 *      date. Default is a separate, later, human decision under maker-checker.
 *   4. REPAYMENT IS A VALUE MOVEMENT. It runs the compliance pipeline and can be refused, so the
 *      screen reports what actually happened rather than assuming the payment landed.
 */

import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  api,
  LipaApiError,
  type CreditLoanDetail,
  type EligibleCollateral,
  type LoanCoverage,
  type MarginCall,
} from '@/lib/api';
import { PageHeader, SectionTitle } from '@/components/dash';
import { StatusPill } from '@/components/status';
import { ErrorState, LoadingCard } from '@/components/states';
import { Reveal } from '@/components/motion';
import { fmtDateShort, fmtDateTime } from '@/lib/format';
import { formatWireAmount, sanitizeAmountTyping } from '@/lib/money';
import { newIdempotencyKey } from '@/lib/ids';

const STATE_LABEL: Record<string, string> = {
  approved: 'Approved, awaiting disbursement',
  disbursed: 'Disbursed',
  repaying: 'Repaying',
  in_arrears: 'Payment overdue',
  in_grace: 'In grace period',
  defaulted: 'In default',
  in_recovery: 'In recovery',
  closed: 'Repaid in full',
  recovered: 'Recovered',
  partially_recovered: 'Partially recovered',
};

export default function CreditLoansId() {
  const { id = '' } = useParams();
  const [loan, setLoan] = useState<CreditLoanDetail | null>(null);
  const [coverage, setCoverage] = useState<LoanCoverage | null>(null);
  const [calls, setCalls] = useState<MarginCall[]>([]);
  const [spare, setSpare] = useState<EligibleCollateral[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [amount, setAmount] = useState('');
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const [detail, cover, eligible] = await Promise.all([
        api.creditMarket.loan(id),
        api.creditMarket.coverage(id),
        api.creditMarket.eligibleCollateral(),
      ]);
      setLoan(detail);
      setCoverage(cover.coverage);
      setCalls(cover.margin_calls);
      // What could be pledged as a top-up. Only assets that are eligible AND have units free: the
      // server refuses anything else, and offering it here would be a remedy that fails on click.
      setSpare(eligible.collateral.filter((c) => c.eligible && c.available_bps > 0));
    } catch (e) {
      setError(e instanceof LipaApiError ? e.detail.message : 'We could not load this loan.');
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  if (error) return <ErrorState body={error} onRetry={() => void load()} />;
  if (!loan) return <LoadingCard lines={5} />;

  const nextDue = loan.schedule.find((s) => s.state !== 'paid');
  const settlement =
    BigInt(loan.total_repayable_minor ?? loan.principal_minor) -
    BigInt(loan.principal_repaid_minor) -
    BigInt(loan.interest_paid_minor) -
    BigInt(loan.fees_paid_minor);
  const payable = ['disbursed', 'repaying', 'in_arrears', 'in_grace'].includes(loan.state);
  const openCall = calls.find((c) => c.state === 'open') ?? null;

  async function topUp(tokenId: string, bps: number) {
    setBusy(true);
    setError(null);
    try {
      await api.creditMarket.topUpCollateral(id, { token_id: tokenId, bps });
      setNotice('Collateral added. Your coverage has been recalculated.');
      await load();
    } catch (e) {
      setError(
        e instanceof LipaApiError ? e.detail.message : 'We could not add that collateral.',
      );
    } finally {
      setBusy(false);
    }
  }

  async function repay(value: string, early: boolean) {
    setBusy(true);
    setError(null);
    try {
      const result = await api.creditMarket.repay(id, value, newIdempotencyKey(), early);
      setLoan(result.loan);
      setNotice(
        result.loan.state === 'closed'
          ? 'Loan fully repaid. Your collateral has been released.'
          : 'Payment received and applied.',
      );
      setAmount('');
    } catch (e) {
      setError(e instanceof LipaApiError ? e.detail.message : 'We could not take that payment.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mx-auto max-w-2xl space-y-5">
      <PageHeader
        title={loan.product_name ?? 'Loan'}
        subtitle={`${loan.lender_name ?? 'Financier'} · ${loan.request_reference ?? ''}`}
        actions={<StatusPill status={loan.state} label={STATE_LABEL[loan.state]} />}
      />

      {notice && (
        <div className="rounded-card border border-brand/30 bg-brand/5 p-3 text-sm text-ink">
          {notice}
        </div>
      )}
      {error && (
        <div
          role="alert"
          className="rounded-card border border-danger/40 bg-danger/5 p-3 text-sm text-ink"
        >
          {error}
        </div>
      )}

      {/* THE MONEY. */}
      <Reveal>
        <section className="rounded-xl bg-gradient-to-br from-primary to-primary-dark p-5 text-white shadow-lifted">
          <p className="text-sm text-white/70">Outstanding principal</p>
          <p className="mt-1 flex items-baseline gap-2">
            <span className="text-sm font-medium text-white/70">KES</span>
            <span className="tabular text-4xl font-semibold tracking-tight">
              {formatWireAmount(loan.outstanding_minor)}
            </span>
          </p>
          <div className="mt-4">
            <div className="flex items-center justify-between text-xs text-white/70">
              <span>Repaid</span>
              <span className="tabular">{loan.repaid_pct}%</span>
            </div>
            <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-white/20">
              <div
                className="h-full rounded-full bg-white/80"
                style={{ width: `${Math.min(100, loan.repaid_pct)}%` }}
              />
            </div>
          </div>
          <dl className="mt-4 grid grid-cols-3 gap-3 border-t border-white/15 pt-3">
            <Hero label="Principal" value={formatWireAmount(loan.principal_minor)} />
            <Hero label="Interest paid" value={formatWireAmount(loan.interest_paid_minor)} />
            <Hero label="Fees paid" value={formatWireAmount(loan.fees_paid_minor)} />
          </dl>
        </section>
      </Reveal>

      {/*
        THE MARGIN CALL (FR-U-CRD-013).

        A demand with a deadline, and it must arrive WITH BOTH REMEDIES attached. A borrower told
        only that they are short has been given a problem; the cure period exists so they can do
        something about it, and the two things they can do are on this panel.

        It says plainly that nothing happens to the asset before the deadline, because the thing a
        borrower does when they think their property is being taken is panic, not act.
      */}
      {openCall && coverage && (
        <Reveal>
          <section className="rounded-card border border-warning/50 bg-warning/5 p-4">
            <p className="text-sm font-medium text-ink">
              The collateral securing this loan has fallen in value
            </p>
            <p className="mt-1.5 text-micro leading-relaxed text-ink-muted">
              Your loan is at {(coverage.ltv_bps / 100).toFixed(1)}% of the collateral&rsquo;s value,
              above the {coverage.margin_call_bps / 100}% limit agreed for this product. To bring it
              back within the limit you can repay part of the balance, or pledge more collateral, by{' '}
              <span className="font-medium text-ink">{fmtDateTime(openCall.cure_deadline)}</span>.
              Nothing happens to your asset before then.
            </p>

            <dl className="mt-3 grid grid-cols-3 gap-3 border-t border-warning/20 pt-3">
              <Fact label="Current LTV" value={`${(coverage.ltv_bps / 100).toFixed(1)}%`} />
              <Fact label="Agreed limit" value={`${coverage.ceiling_bps / 100}%`} />
              <Fact
                label="To repay"
                value={`KES ${formatWireAmount(coverage.shortfall_minor)}`}
              />
            </dl>

            <div className="mt-3 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() =>
                  setAmount((Number(coverage.shortfall_minor) / 100).toFixed(2))
                }
                className="rounded-field bg-primary px-3 py-1.5 text-micro font-medium text-white"
              >
                Repay KES {formatWireAmount(coverage.shortfall_minor)}
              </button>
              {spare.map((c) => (
                <button
                  key={c.token_id}
                  type="button"
                  disabled={busy}
                  onClick={() => void topUp(c.token_id, c.available_bps)}
                  className="rounded-field border border-border bg-surface px-3 py-1.5 text-micro font-medium text-ink disabled:opacity-40"
                >
                  Pledge {(c.available_bps / 100).toFixed(0)}% of {c.asset_name}
                </button>
              ))}
            </div>
          </section>
        </Reveal>
      )}

      {/* Coverage, shown whether or not anything is wrong with it. */}
      {coverage && !openCall && ['disbursed', 'repaying'].includes(loan.state) && (
        <section className="rounded-card border border-border bg-surface p-4">
          <p className="text-sm font-medium text-ink">Collateral coverage</p>
          <dl className="mt-2.5 grid grid-cols-3 gap-3">
            <Fact label="Current LTV" value={`${(coverage.ltv_bps / 100).toFixed(1)}%`} />
            <Fact label="Agreed limit" value={`${coverage.ceiling_bps / 100}%`} />
            <Fact
              label="Collateral value"
              value={`KES ${formatWireAmount(coverage.eligible_minor)}`}
            />
          </dl>
          <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-surface-sunken">
            <div
              className={`h-full rounded-full ${
                coverage.ltv_bps > coverage.margin_call_bps ? 'bg-warning' : 'bg-brand'
              }`}
              style={{ width: `${Math.min(100, (coverage.ltv_bps / 10000) * 100)}%` }}
            />
          </div>
          <p className="mt-2 text-nano leading-relaxed text-ink-subtle">
            If the value of your collateral falls far enough that this passes{' '}
            {coverage.margin_call_bps / 100}%, we will ask you to repay some of the balance or pledge
            more, and give you time to do it.
          </p>
        </section>
      )}

      {/* The history of calls, so a cured one is still visible afterwards. */}
      {calls.filter((c) => c.state !== 'open').length > 0 && (
        <section>
          <SectionTitle>Margin call history</SectionTitle>
          <ul className="space-y-2">
            {calls
              .filter((c) => c.state !== 'open')
              .map((c) => (
                <li
                  key={c.id}
                  className="flex items-start justify-between gap-3 rounded-card border border-border bg-surface p-3.5"
                >
                  <div className="min-w-0">
                    <p className="text-micro text-ink">
                      Issued {fmtDateShort(c.issued_at)} at{' '}
                      {(c.ltv_at_issue_bps / 100).toFixed(1)}% LTV
                    </p>
                    <p className="text-nano text-ink-subtle">
                      Shortfall KES {formatWireAmount(c.shortfall_minor)}
                    </p>
                  </div>
                  <StatusPill status={c.state} />
                </li>
              ))}
          </ul>
        </section>
      )}

      {/* ARREARS. Overdue is not default, and the screen has to say which one this is. */}
      {['in_arrears', 'in_grace'].includes(loan.state) && (
        <div className="rounded-card border border-warning/40 bg-warning/5 p-3.5">
          <p className="text-sm font-medium text-ink">
            {loan.state === 'in_grace' ? 'You are in the grace period' : 'A payment is overdue'}
          </p>
          <p className="mt-1 text-micro leading-relaxed text-ink-muted">
            {loan.arrears_since && <>Overdue since {fmtDateShort(loan.arrears_since)}. </>}
            {loan.grace_expires_at ? (
              <>
                Pay by {fmtDateShort(loan.grace_expires_at)} to bring the loan back to normal. This
                is not a default, and no enforcement can begin before then.
              </>
            ) : (
              <>Pay as soon as you can to bring the loan back to normal.</>
            )}
          </p>
        </div>
      )}

      {loan.state === 'approved' && (
        <div className="rounded-card border border-info/30 bg-info/5 p-3 text-sm text-ink-muted">
          <span className="font-medium text-info">Approved.</span> The financier has approved this
          loan and your collateral is held. The funds have not been disbursed yet, so nothing is owed.
        </div>
      )}

      {/* PAY. */}
      {payable && (
        <section className="rounded-card border border-border bg-surface p-4">
          <p className="text-sm font-medium text-ink">Make a repayment</p>
          {nextDue && (
            <p className="mt-1 text-micro text-ink-muted">
              Next payment KES {formatWireAmount(nextDue.total_minor)} due{' '}
              {fmtDateShort(nextDue.due_at)}.
            </p>
          )}
          <div className="mt-3 flex items-baseline gap-2 rounded-field bg-surface-sunken px-3 py-2.5">
            <span className="text-sm text-ink-muted">KES</span>
            <input
              inputMode="decimal"
              value={amount}
              onChange={(e) => setAmount(sanitizeAmountTyping(e.target.value))}
              placeholder="0.00"
              className="tabular w-full bg-transparent text-xl font-semibold text-ink outline-none placeholder:text-ink-subtle"
            />
          </div>
          <div className="mt-2.5 flex flex-wrap gap-2">
            {nextDue && (
              <button
                type="button"
                onClick={() => setAmount((Number(nextDue.total_minor) / 100).toFixed(2))}
                className="rounded-field bg-surface-sunken px-3 py-1.5 text-micro font-medium text-ink"
              >
                Next instalment
              </button>
            )}
            <button
              type="button"
              onClick={() => setAmount((Number(settlement) / 100).toFixed(2))}
              className="rounded-field bg-surface-sunken px-3 py-1.5 text-micro font-medium text-ink"
            >
              Settle in full (KES {formatWireAmount(String(settlement))})
            </button>
          </div>
          <button
            type="button"
            disabled={busy || !amount}
            onClick={() => void repay(amount, BigInt(Math.round(Number(amount) * 100)) >= settlement)}
            className="mt-3 w-full rounded-field bg-primary px-3.5 py-2 text-sm font-medium text-white transition hover:bg-primary-light disabled:opacity-40"
          >
            {busy ? 'Paying…' : 'Pay'}
          </button>
          <p className="mt-2 text-nano leading-relaxed text-ink-subtle">
            Payments are applied to fees first, then interest, then principal. Settling early rebates
            the interest you have not yet incurred.
          </p>
        </section>
      )}

      {/* THE SCHEDULE. */}
      <section>
        <SectionTitle>Repayment schedule</SectionTitle>
        <ul className="divide-y divide-border overflow-hidden rounded-card border border-border bg-surface">
          {loan.schedule.map((s) => (
            <li key={s.n} className="flex items-center justify-between gap-3 px-3.5 py-2.5">
              <div className="min-w-0">
                <p className="text-micro font-medium text-ink">
                  {s.n}. {fmtDateShort(s.due_at)}
                </p>
                <p className="text-nano text-ink-subtle">
                  KES {formatWireAmount(s.principal_minor)} principal + KES{' '}
                  {formatWireAmount(s.interest_minor)} interest
                </p>
              </div>
              <div className="shrink-0 text-right">
                <p className="tabular text-micro font-medium text-ink">
                  KES {formatWireAmount(s.total_minor)}
                </p>
                <StatusPill status={s.state} />
              </div>
            </li>
          ))}
        </ul>
      </section>

      {/* THE COLLATERAL LOCK (FR-U-CRD-006). */}
      <section>
        <SectionTitle>Collateral</SectionTitle>
        {loan.collateral.length === 0 ? (
          <p className="rounded-card bg-surface-sunken p-3 text-micro text-ink-muted">
            The collateral for this loan has been released.
          </p>
        ) : (
          <ul className="space-y-2">
            {loan.collateral.map((c) => (
              <li
                key={c.id}
                className="rounded-card border border-border bg-surface p-3.5 shadow-card"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-ink">{c.asset_name}</p>
                    <p className="text-nano text-ink-subtle">
                      {(c.pledged_bps / 100).toFixed(2)}% of the asset ·{' '}
                      {c.released_at
                        ? `released ${fmtDateShort(c.released_at)}`
                        : 'locked for the life of the loan'}
                    </p>
                  </div>
                  <StatusPill
                    status={c.state}
                    label={c.state === 'encumbered' ? 'Held by the Trust' : undefined}
                  />
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* WHAT HAS BEEN PAID, AND HOW IT WAS APPLIED. */}
      {loan.repayments.length > 0 && (
        <section>
          <SectionTitle>Payments</SectionTitle>
          <ul className="space-y-2">
            {loan.repayments.map((r) => (
              <li key={r.id} className="rounded-card border border-border bg-surface p-3.5">
                <div className="flex items-baseline justify-between gap-3">
                  <span className="tabular text-sm font-medium text-ink">
                    KES {formatWireAmount(r.amount_minor)}
                  </span>
                  <span className="text-nano text-ink-subtle">{fmtDateTime(r.paid_at)}</span>
                </div>
                <p className="mt-1 text-nano text-ink-muted">
                  KES {formatWireAmount(r.applied_fees_minor)} fees · KES{' '}
                  {formatWireAmount(r.applied_interest_minor)} interest · KES{' '}
                  {formatWireAmount(r.applied_principal_minor)} principal
                  {r.is_early_settlement && <> · early settlement</>}
                </p>
              </li>
            ))}
          </ul>
        </section>
      )}

      <Link
        to="/credit"
        className="inline-block rounded-field border border-border px-3.5 py-2 text-sm font-medium text-ink"
      >
        Back to Credit
      </Link>
    </div>
  );
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-nano text-ink-subtle">{label}</dt>
      <dd className="tabular truncate text-micro font-medium text-ink">{value}</dd>
    </div>
  );
}

function Hero({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-nano text-white/60">{label}</dt>
      <dd className="tabular truncate text-sm font-semibold">KES {value}</dd>
    </div>
  );
}
